import React, { useState, forwardRef, useImperativeHandle } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  useMediaQuery,
} from "@mui/material";
import discountIcon from "../../../assets/svg/discount.svg";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import ConfettiExplosion from "react-confetti-explosion";
import { motion } from "framer-motion";
import CelebrationIcon from "@mui/icons-material/Celebration";

const CouponCelebrationDialog = forwardRef(
  ({ save, couponCode, applicableCount, endDate }, ref) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const [isExploding, setIsExploding] = useState(true);

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    useImperativeHandle(ref, () => ({
      openDialog: () => setOpen(true),
      closeDialog: () => setOpen(false),
    }));

    const formattedEndDate = endDate
      ? new Date(endDate).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : null;

    return (
      <>
        <Dialog
          fullWidth
          open={open}
          maxWidth="xs"
          onClose={() => setOpen(false)}
          sx={dialogStyles}
        >
          {/* Content Section */}
          <DialogContent>
            {isExploding && <ConfettiExplosion zIndex={9999} duration={3000} />}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <motion.img
                animate={{
                  rotate: [0, 60, 0],
                }}
                transition={{
                  duration: 4,
                  ease: "easeInOut",
                  repeatType: "reverse",
                }}
                src={discountIcon}
                alt="Coupon Icon"
                style={{ height: "104px", width: "104px", margin: "auto" }}
              />
              <Box>
                {/* Saved amount */}
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "32px",
                    lineHeight: "125%",
                    textAlign: "center",
                    color: "#0cb67b",
                  }}
                >
                  ₹{save}
                </Typography>

                {/* Dynamic coupon code */}
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "150%",
                    textAlign: "center",
                    color: "var(--neutral-800)",
                  }}
                >
                  Saved using {couponCode}
                </Typography>

                {/* Remaining usage count */}
                {applicableCount != null && applicableCount > 0 && (
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "13px",
                      lineHeight: "150%",
                      textAlign: "center",
                      color: "var(--neutral-700)",
                      mt: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    Your favorite is waiting! Save more with {applicableCount}{" "}
                    use{applicableCount > 1 ? "s" : ""} left
                    <motion.div
                      animate={{ rotate: [0, -15, 15, -10, 10, 0] }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        repeatDelay: 2,
                      }}
                      style={{
                        display: "inline-flex",
                        verticalAlign: "middle",
                      }}
                    >
                      <CelebrationIcon
                        sx={{
                          fontSize: 16,
                          color: "var(--primary-red-base)",
                        }}
                      />
                    </motion.div>
                  </Typography>
                )}

                {/* Expiry date — nudge to reorder */}
                {formattedEndDate &&
                  applicableCount != null &&
                  applicableCount > 0 && (
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "13px",
                        lineHeight: "150%",
                        textAlign: "center",
                        color: "var(--neutral-700)",
                        mt: 0.5,
                      }}
                    >
                      <motion.div
                        animate={{ rotate: [0, -15, 15, -10, 10, 0] }}
                        transition={{
                          duration: 0.8,
                          repeat: Infinity,
                          repeatDelay: 2,
                        }}
                        style={{
                          display: "inline-flex",
                          verticalAlign: "middle",
                        }}
                      >
                        <AccessTimeIcon
                          sx={{
                            mr: 0.5,
                            fontSize: 16,
                            color: "var(--primary-red-base)",
                          }}
                        />
                      </motion.div>
                      Valid till {formattedEndDate} — Don't miss it!
                    </Typography>
                  )}
              </Box>

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "150%",
                  textAlign: "center",
                  color: "var(--neutral-black)",
                }}
              >
                Congratulations
              </Typography>
              <Button
                onClick={() => setOpen(false)}
                variant="outlined"
                sx={{
                  py: 1,
                  px: 2,
                  fontSize: "14px",
                  fontWeight: 600,
                  width: "100%",
                  color: "var(--primary-red-base)",
                  mt: 4,
                }}
              >
                Continue
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

const AddonItem = () => {
  return (
    <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} sx={addonItemContainer}>
      <img
        src="https://res.cloudinary.com/blkfor/image/upload/v1710325448/BlaackForrest/Addons/Party%20Popper.jpg"
        alt="Party Popper"
        style={addonImage}
      />
      <Typography sx={addonTitle}>Party popper small</Typography>
      <Typography sx={addonPrice}>₹60</Typography>
      <Button variant="outlined" sx={addButton}>
        Add
      </Button>
    </Grid>
  );
};

// Styles
const dialogStyles = {
  "& .MuiPaper-root": {
    backgroundColor: "var(--neutral-100)",
    borderRadius: "8px",
  },
};

const headerContainer = {
  display: "flex",
  flexDirection: "row",
  position: "relative",
  alignItems: "center",
  justifyContent: "center",
};

const titleStyles = {
  textAlign: "center",
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: "150%",
  color: "var(--neutral-black)",
};

const closeButtonStyles = {
  color: "var(--neutral-black)",
  position: "absolute",
  right: "24px",
};

const priceDetailsContainer = {
  backgroundColor: "var(--neutral-white)",
  p: 3,
  mt: 3,
  height: "96px",
  borderRadius: "8px",
  alignItems: "center",
  justifyContent: "space-between",
  display: "flex",
};

const priceDetailsBox = {
  py: 3,
  height: "96px",
  borderRadius: "8px",
  alignItems: "center",
  justifyContent: "space-between",
  display: "flex",
  width: "60%",
};

const bottomValue = {
  fontSize: 16,
  fontWeight: 600,
  color: "var(--neutral-black)",
  paddingLeft: "12px",
};

const bottomLabel = {
  fontSize: 16,
  fontWeight: 500,
  color: "var(--neutral-700)",
  lineHeight: "150%",
};

const addonItemContainer = {
  border: "1px solid var(--neutral-300)",
  borderRadius: "8px",
  width: "160px",
  height: "200px",
  background: "var(--neutral-white)",
  p: 1,
  gap: 2,
};

const addonImage = {
  borderRadius: "4px",
  backgroundColor: "#eef0ff",
  width: "100%",
  height: "96px",
};

const addonTitle = {
  fontWeight: 600,
  fontSize: "12px",
  color: "var(--neutral-800)",
  lineHeight: "167%",
};

const addonPrice = {
  fontWeight: 600,
  fontSize: "14px",
  color: "var(--neutral-black)",
  lineHeight: "157%",
};

const addButton = {
  mt: 1,
  fontSize: "14px",
  fontWeight: 600,
  width: "100%",
  borderRadius: "4px",
  height: "32px",
  border: "1px solid var(--primary-red-base)",
};

const addItemsButton = {
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "8px",
  height: "48px",
  px: 12,
};

export default CouponCelebrationDialog;
